import React from 'react';
import { useNavigate } from 'react-router-dom';
import { VersionBadge } from '@odo/shared-ui';
import { Avatar } from '../../Avatar';
import { usePatientMetadata } from '../../../providers/PatientMetadataProvider';
import type { PatientListItemType } from '../../../providers/patient/PatientsListProvider';
import { getNameClickRoute } from '../../../utils/actionRoutes';

interface PatientCellProps {
    patient: PatientListItemType;
    /** Only true when the practice has patients on both flows - see
     *  hasMixedPatientVersions in PatientsListProvider. */
    showVersionBadge: boolean;
}

/** Avatar + name, with a hover tooltip pointing at the treatment progress page. */
export function PatientCell({ patient, showVersionBadge }: PatientCellProps) {
    const navigate = useNavigate();
    const [showTooltip, setShowTooltip] = React.useState(false);
    const { getProfilePictureUrl, getProfileThumbnailUrl } = usePatientMetadata();

    const profilePictureUrl = getProfilePictureUrl(patient.id);
    const profileThumbnailUrl = getProfileThumbnailUrl(patient.id);

    return (
        <div className="flex items-center gap-4 min-w-0">
            <div className="w-8 h-8 flex-shrink-0">
                <Avatar
                    user={{ name: patient.name ?? '', email: patient.email ?? '' }}
                    imageURL={profilePictureUrl ?? ''}
                    thumbnailURL={profileThumbnailUrl ?? undefined}
                />
            </div>
            <div className="relative min-w-0 flex items-center gap-2">
                <span
                    // min-w-0 is load-bearing: as a flex item this span's automatic
                    // minimum size is its nowrap min-content width, so without it
                    // truncate never engages and long names overflow the column.
                    className="block min-w-0 truncate text-sm font-normal leading-4 tracking-[0.005em] text-gray-800 underline cursor-pointer"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    onClick={() =>
                        navigate(getNameClickRoute(patient.requiredAction ?? null, patient.id))
                    }
                >
                    {patient.name}
                </span>
                {showVersionBadge && (
                    <VersionBadge value={patient.version} className="flex-shrink-0" />
                )}
                {showTooltip && (
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-50">
                        <div className="bg-gray-900 px-2.5 py-2 rounded shadow-lg">
                            <p className="text-white text-sm font-medium leading-5 tracking-[0.07px] whitespace-nowrap">
                                View treatment progress
                            </p>
                        </div>
                        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                            <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-gray-900"></div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
